// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import type { MetricsContext } from './dashboardMetrics';
import type { ChatChartSpec } from '../chatbot/chatbotTypes';

// "2.0" (a float key that Python str()'d server-side) and 2 (a JS number layer attribute → "2") are the SAME
// feature — strip a trailing all-zero fraction from both sides so the key join doesn't silently miss. Targeted
// on purpose: it leaves non-numeric keys ("P-123"), big integers, and leading zeros ("007") untouched, avoiding
// the precision loss / false collapses of a Number() round-trip.
const canonicalKey = (key: string | number): string => String(key).replace(/\.0+$/, '');

// Re-apply the chart's aggregate over the in-view features of one category. count/sum of an empty in-view set
// are well-defined (0); avg/min/max of nothing are undefined → null (a gap, not a misleading 0).
const aggregateInView = (aggregate: string, measures: number[], count: number): number | null => {
  if (aggregate === 'count') return count;
  if (aggregate === 'sum') return measures.reduce((total, value) => total + value, 0);
  if (measures.length === 0) return null;
  if (aggregate === 'avg') return measures.reduce((total, value) => total + value, 0) / measures.length;
  // reduce, not Math.min(...)/Math.max(...): spreading a very large in-view category throws a RangeError, and
  // it would throw OUTSIDE the query try/catch → the recompute rejects and stale numbers stay under the badge.
  if (aggregate === 'min') return measures.reduce((low, value) => (value < low ? value : low), measures[0]);
  if (aggregate === 'max') return measures.reduce((high, value) => (value > high ? value : high), measures[0]);
  return null;
};

// Re-aggregate a single-layer chart against the current map extent — the chart analogue of the widget view-only
// recompute in dashboardMetrics.ts. The chart's `recompute` descriptor names {layer, key_field, aggregate}; each
// row carries `_keys` (its category's feature keys) and, for a measure aggregate, `_key_values` (the per-feature
// measure, index-aligned). We fetch the keys of features in the extent and, per category, re-apply the aggregate
// over the in-view subset. Holds for derived categories (bands/CASE) since the binning is already in `_keys`.
//
// Returns null when it can't run (missing layer / no measure field / truncated or errored query / the key spaces
// don't align) so the caller falls back to the baked full-extent data rather than showing wrong numbers.
// eslint-disable-next-line import/prefer-default-export
export const recomputeAggregateChart = async (
  chart: ChatChartSpec,
  ctx: MetricsContext,
): Promise<ChatChartSpec['data'] | null> => {
  const spec = chart.recompute;
  const measure = chart.y_field;
  if (!spec || !measure) return null;
  const layer = ctx.layers.get(spec.layer);
  if (!layer) return null;

  const query = layer.createQuery();
  query.outFields = [spec.key_field];
  query.returnGeometry = false; // attribute-only: we only need which keys are in view, not their geometry
  if (ctx.view.extent) {
    query.geometry = ctx.view.extent;
    query.spatialRelationship = 'intersects';
  } else {
    query.where = '1=1';
  }

  let visibleKeys: Set<string>;
  try {
    await layer.load();
    const result = await layer.queryFeatures(query);
    // A truncated query (a hosted FeatureLayer past its maxRecordCount) would undercount — fall back to
    // full-extent rather than present a low value as "current view". (Client-side GeoJSONLayers never truncate.)
    if (result.exceededTransferLimit) return null;
    visibleKeys = new Set(result.features.map((feature) => canonicalKey(feature.attributes[spec.key_field])));
  } catch {
    return null; // fall back to the baked full-extent data
  }

  const { aggregate } = spec;
  let matched = 0;
  const perRow = chart.data.map((row) => {
    const keys = row._keys ?? [];
    const values = row._key_values ?? [];
    const inViewKeys: string[] = [];
    const inViewMeasures: number[] = [];
    keys.forEach((key, index) => {
      if (visibleKeys.has(canonicalKey(key))) {
        inViewKeys.push(key);
        if (aggregate !== 'count') inViewMeasures.push(Number(values[index]));
      }
    });
    matched += inViewKeys.length;
    return { row, inViewKeys, value: aggregateInView(aggregate, inViewMeasures, inViewKeys.length) };
  });

  // Key-join sanity: if the layer HAS features in the extent but NONE matched the chart's keys, the key spaces
  // don't align (a client-regenerated OBJECTID, say) — a silent mismatch, not an empty view. Fall back rather
  // than badge an all-zero chart "current view". A genuine empty view (no features in the extent) keeps zeros.
  if (visibleKeys.size > 0 && matched === 0) return null;

  // Each row keeps its category, gets the re-aggregated measure, and its `_keys` filtered to the in-view subset
  // (so a bar's value and its click-to-highlight match). TS can't verify a computed-key spread against the row's
  // `Record<string,string|number> & {_keys?}` intersection, though the runtime shape is a valid chart row.
  return perRow.map(({ row, inViewKeys, value }) => ({
    ...row,
    [measure]: value,
    _keys: inViewKeys,
  })) as unknown as ChatChartSpec['data'];
};
